// src/pages/settings/themes/ThemeSwitcher.tsx
import React from "react";
import { Row, Col, Space, Typography, Segmented, Tooltip } from "antd";
import { 
  BulbOutlined, 
  BulbFilled,
  CheckOutlined
} from '@ant-design/icons';
import { UserPreferences } from "../../../utils/userPrefs/UserPrefs";

const { Text } = Typography;

interface ThemeSwitcherProps {
  preferences: UserPreferences;
  updatePreference: (key: keyof UserPreferences, value: any) => void;
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ preferences, updatePreference }) => {
  // Accent color options
  const accentColors = [
    // Blues
    { name: 'Blue', value: 'blue', color: '#1890ff' },
    { name: 'Navy Blue', value: 'navyBlue', color: '#003a8c' },
    { name: 'Sky Blue', value: 'skyBlue', color: '#40a9ff' },
    { name: 'Ocean Blue', value: 'oceanBlue', color: '#006d75' },
    // Indigo & Violet
    { name: 'Indigo', value: 'indigo', color: '#2f54eb' },
    { name: 'Purple', value: 'purple', color: '#722ed1' },
    { name: 'Violet', value: 'violet', color: '#531dab' },
    { name: 'Lavender', value: 'lavender', color: '#b37feb' },
    // Greens
    { name: 'Green', value: 'green', color: '#52c41a' },
    { name: 'Forest Green', value: 'forestGreen', color: '#135200' },
    { name: 'Mint Green', value: 'mintGreen', color: '#36cfc9' },
    { name: 'Sage Green', value: 'sageGreen', color: '#5f9e6e' },
    // Teals & Cyans
    { name: 'Teal', value: 'teal', color: '#13c2c2' },
    { name: 'Cyan', value: 'cyan', color: '#0891b2' },
    // Reds & Warm
    { name: 'Red', value: 'red', color: '#f5222d' },
    { name: 'Crimson', value: 'crimson', color: '#a8071a' },
    { name: 'Coral', value: 'coral', color: '#ff7a45' },
    { name: 'Orange', value: 'orange', color: '#fa8c16' },
    // Yellows
    { name: 'Amber', value: 'amber', color: '#faad14' },
    { name: 'Gold', value: 'gold', color: '#d4b106' },
    // Pinks
    { name: 'Pink', value: 'pink', color: '#eb2f96' },
    { name: 'Rose', value: 'rose', color: '#f43f5e' },
    { name: 'Baby Pink', value: 'babyPink', color: '#ffd1dc' },
    // Neutrals
    { name: 'Slate', value: 'slate', color: '#475569' },
    { name: 'Charcoal', value: 'charcoal', color: '#374151' },
  ];

  // Theme mode options
  const themeModeOptions = [
    {
      label: (
        <span>
          <BulbOutlined style={{ marginRight: 8 }} />
          Light
        </span>
      ),
      value: 'light',
    },
    {
      label: (
        <span>
          <BulbFilled style={{ marginRight: 8 }} />
          Dark
        </span>
      ),
      value: 'dark',
    },
  ];

  return (
    <Row gutter={[24, 24]}>
      {/* Theme Mode Selection */}
      <Col xs={24} md={12}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ fontSize: 16 }}>Theme Mode</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 13 }}>
              Choose between light and dark appearance
            </Text>
          </div>
          <Segmented
            value={preferences.theme}
            options={themeModeOptions}
            onChange={(value) => updatePreference("theme", value)}
            size="large"
            style={{ width: '100%' }}
          />
        </Space>
      </Col>

      {/* Accent Color Selection */}
      <Col xs={24}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ fontSize: 16 }}>Accent Color</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 13 }}>
              Select your preferred theme color
            </Text>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {accentColors.map((color) => {
              const isSelected = preferences.accentColor === color.value;
              // Use white check on dark swatches, dark check on very light ones
              const isLight = ['babyPink', 'lavender', 'amber', 'gold'].includes(color.value);
              return (
                <Tooltip key={color.value} title={color.name} mouseEnterDelay={0.4}>
                  <div
                    onClick={() => updatePreference('accentColor', color.value)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: color.color,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isSelected
                        ? `0 0 0 3px white, 0 0 0 5px ${color.color}`
                        : '0 1px 3px rgba(0,0,0,0.25)',
                      transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                      transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                    }}
                  >
                    {isSelected && (
                      <CheckOutlined
                        style={{
                          color: isLight ? '#333' : '#fff',
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </div>
                </Tooltip>
              );
            })}
          </div>

          {/* Selected color label */}
          {preferences.accentColor && (() => {
            const selected = accentColors.find(c => c.value === preferences.accentColor);
            return selected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: selected.color,
                    flexShrink: 0,
                  }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>{selected.name}</Text>
              </div>
            ) : null;
          })()}
        </Space>
      </Col>
    </Row>
  );
};

export default ThemeSwitcher;